// planificationHistory.js — Phase 2A foundations (Iter95) -> wired to
// real Undo/Redo/Clear-all UI commands in Phase 2C (Iter99, spec §6).
//
// Undo/Redo ARCHITECTURE for Planification (spec §7/§6). This module
// only defines the data contract and pure transition functions — Terra
// Draw synchronization on undo/redo (spec §6.6: "Synchroniser Terra
// Draw") is the CALLER's responsibility (planificationSession.js +
// PlanificationTab.jsx), since this module has no map/draw instance.
//
// Contract (verbatim from the Phase 2A spec):
//  - Max 20 COMPLETED actions kept (oldest dropped once the cap is hit).
//  - A continuous geometry edit (e.g. dragging one vertex around) counts
//    as ONE action — callers must call `recordAction` once per commit,
//    not per intermediate pointer-move frame.
//  - create / update / delete are all historised.
//  - Any new recordAction after an undo() invalidates the redo stack.
//  - Restored objects keep their original UUID (the `before`/`after`
//    snapshots below are full Features, id included, never regenerated).
//  - Canceling an IN-PROGRESS draw (e.g. Escape before a polygon is
//    closed) must NEVER call `recordAction` — it is not a completed
//    action. It must not touch the undo stack, not consume one of the 20
//    slots, not delete already-validated objects, and not invalidate an
//    existing redo stack. Enforcing this is the CALLER's responsibility
//    (only call `recordAction` from a Terra Draw `finish` event, never
//    from a `change`/provisional event) — this module has no way to
//    observe map events itself, so it only documents + types the rule.

export const MAX_HISTORY_SIZE = 20;

export const ACTION_TYPES = Object.freeze({
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete",
  // Phase 2C (spec §6.2/§8.5): "Effacer tous les dessins" is historised
  // as ONE logical action, not N individual deletes — `before` holds the
  // FULL features array pre-clear (not a single Feature), `after` is
  // always `[]`. `featureId` is `null` (it targets the whole collection).
  CLEAR_ALL: "clear_all",
});

/**
 * @typedef {Object} PlanificationAction
 * @property {"create"|"update"|"delete"|"clear_all"} type
 * @property {string|null} featureId - null only for "clear_all"
 * @property {object|object[]|null} before - full Feature snapshot pre-action
 *   (null for "create"; the full features ARRAY, not one Feature, for "clear_all")
 * @property {object|object[]|null} after  - full Feature snapshot post-action
 *   (null for "delete"; always `[]` for "clear_all")
 */

export const createHistory = () => ({ undoStack: [], redoStack: [] });

/**
 * Records one COMPLETED action. Immutable — returns a new history object.
 * Drops the oldest entry once `undoStack` exceeds MAX_HISTORY_SIZE, and
 * always clears `redoStack` (a fresh action invalidates any pending redo).
 */
export const recordAction = (history, action) => {
  const undoStack = [...history.undoStack, action].slice(-MAX_HISTORY_SIZE);
  return { undoStack, redoStack: [] };
};

export const canUndo = (history) => history.undoStack.length > 0;
export const canRedo = (history) => history.redoStack.length > 0;

/**
 * Pops the most recent action off `undoStack` onto `redoStack`.
 * Returns `{ history, action }` — `action` is the one to REVERSE (apply
 * its `before` snapshot); `action` is `null` if the stack was empty.
 */
export const undo = (history) => {
  if (!canUndo(history)) return { history, action: null };
  const action = history.undoStack[history.undoStack.length - 1];
  return {
    history: {
      undoStack: history.undoStack.slice(0, -1),
      redoStack: [...history.redoStack, action],
    },
    action,
  };
};

/**
 * Pops the most recent action off `redoStack` back onto `undoStack`.
 * Returns `{ history, action }` — `action` is the one to RE-APPLY (apply
 * its `after` snapshot); `action` is `null` if the stack was empty.
 */
export const redo = (history) => {
  if (!canRedo(history)) return { history, action: null };
  const action = history.redoStack[history.redoStack.length - 1];
  return {
    history: {
      undoStack: [...history.undoStack, action].slice(-MAX_HISTORY_SIZE),
      redoStack: history.redoStack.slice(0, -1),
    },
    action,
  };
};
